import {
  isListTheme,
  LIST_FONTS,
  LIST_TEMPLATES,
  TEMPLATE_CONFIG,
  THEME_PALETTES,
} from "@listou/shared/constants";
import { MOTIF_LAYOUTS, STICKER_PACKS } from "@listou/shared/stickers";
import type { AdminDesignCount, AdminDesignsResponse } from "@listou/shared/types/api";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { Split } from "../components/admin/charts";
import {
  Box,
  Grid,
  n,
  Pending,
  percent,
  Row,
  Table,
  useAdminDays,
} from "../components/admin/retro";
import { adminDesignsQuery } from "../lib/queries";
import { FONT_LABELS } from "../lib/theme";

export const Route = createFileRoute("/_authed/admin/designs")({
  component: DesignsPage,
});

const templateLabel = (key: string | null) => {
  const template = LIST_TEMPLATES.find((known) => known === key);
  return template ? TEMPLATE_CONFIG[template].label : (key ?? "aucun");
};

const fontLabel = (key: string | null) => {
  const font = LIST_FONTS.find((known) => known === key);
  return font ? FONT_LABELS[font].label : (key ?? "aucune");
};

const packLabel = (key: string | null) =>
  key === null ? "aucun" : (STICKER_PACKS.find((pack) => pack.id === key)?.label ?? key);

const layoutLabel = (key: string | null) =>
  MOTIF_LAYOUTS.find((layout) => layout.id === key)?.label ?? key ?? "aucune";

/** The palette's own colours, so a theme reads at a glance and not only by name. */
function ThemeLabel({ id }: { id: string | null }) {
  if (!id || !isListTheme(id)) return <>{id ?? "?"}</>;
  const palette = THEME_PALETTES[id];
  return (
    <span>
      <span style={{ color: palette.background }}>█</span>
      <span style={{ color: palette.accent }}>█</span>
      <span style={{ color: palette.ink }}>█</span> {palette.label}
    </span>
  );
}

/** A share bar as wide as the column allows, in text like everything else here. */
const bar = (part: number, whole: number, width = 16) => {
  const filled = whole > 0 ? Math.round((part / whole) * width) : 0;
  return (
    <span>
      <span style={{ color: "var(--r-amber)" }}>{"█".repeat(filled)}</span>
      <span style={{ color: "var(--r-line)" }}>{"░".repeat(width - filled)}</span>
    </span>
  );
};

function DesignBox({
  title,
  counts,
  total,
  recentTotal,
  label,
  plainLabel,
  className,
}: {
  title: string;
  counts: AdminDesignCount[];
  total: number;
  recentTotal: number;
  label: (key: string | null) => ReactNode;
  /** For the split bar's legend, where a swatch-and-name label would not fit. */
  plainLabel: (key: string | null) => string;
  className?: string;
}) {
  return (
    <Box title={title} className={className}>
      <div className="mb-2">
        <Split
          parts={[
            ...counts.slice(0, 5).map((count) => ({
              label: plainLabel(count.key),
              values: [count.lists],
            })),
            ...(counts.length > 5
              ? [
                  {
                    label: "autres",
                    values: [counts.slice(5).reduce((sum, count) => sum + count.lists, 0)],
                    color: "var(--r-line)",
                  },
                ]
              : []),
          ]}
        />
      </div>
      <Table
        head={[
          "valeur",
          "listes",
          "part",
          "",
          "récentes",
          "gardé tel quel",
          "envies/liste",
          "clics",
        ]}
        rows={counts.map((count) => [
          { value: label(count.key), num: false },
          count.lists,
          percent(count.lists, total),
          { value: bar(count.lists, total), num: false },
          {
            value: `${n(count.recent)} (${percent(count.recent, recentTotal)})`,
            tone: count.recent > 0 ? undefined : "dim",
          },
          { value: percent(count.defaulted, count.lists), tone: "dim" },
          count.lists > 0 ? (count.wishes / count.lists).toFixed(1) : "-",
          count.clicks,
        ])}
      />
    </Box>
  );
}

function DesignsPage() {
  const days = useAdminDays();
  const designs = useQuery(adminDesignsQuery(days));
  if (!designs.data) return <Pending query={designs} />;
  return (
    <div className={designs.isPlaceholderData ? "opacity-60" : undefined}>
      <Designs designs={designs.data} />
    </div>
  );
}

function Designs({ designs }: { designs: AdminDesignsResponse }) {
  const total = designs.lists;
  const recentTotal = designs.templates.reduce((sum, count) => sum + count.recent, 0);
  const common = { total, recentTotal };
  return (
    <Grid>
      <Box title="résumé" right={`récentes = ${designs.days} j`}>
        <Row label="listes">{n(total)}</Row>
        <Row label={`créées en ${designs.days} j`}>{n(recentTotal)}</Row>
        <Row label="look personnalisé">
          {n(designs.customized)} ({percent(designs.customized, total)})
        </Row>
        <Row label="look de l'occasion gardé">
          {n(total - designs.customized)} ({percent(total - designs.customized, total)})
        </Row>
        <p className="mt-1" style={{ color: "var(--r-dim)" }}>
          « Gardé tel quel » : la valeur que l'occasion donne à la création, jamais changée.
        </p>
      </Box>
      <DesignBox
        title="modèles"
        counts={designs.templates}
        label={templateLabel}
        plainLabel={templateLabel}
        className="xl:col-span-3"
        {...common}
      />
      <DesignBox
        title="thèmes"
        counts={designs.themes}
        label={(key) => <ThemeLabel id={key} />}
        plainLabel={(key) => (key && isListTheme(key) ? THEME_PALETTES[key].label : (key ?? "?"))}
        className="md:col-span-2"
        {...common}
      />
      <DesignBox
        title="polices"
        counts={designs.fonts}
        label={fontLabel}
        plainLabel={fontLabel}
        className="md:col-span-2"
        {...common}
      />
      <DesignBox
        title="stickers"
        counts={designs.stickerPacks}
        label={packLabel}
        plainLabel={packLabel}
        className="md:col-span-2"
        {...common}
      />
      <DesignBox
        title="motif de fond"
        counts={designs.motifs}
        label={packLabel}
        plainLabel={packLabel}
        className="md:col-span-2"
        {...common}
      />
      <DesignBox
        title="disposition du motif"
        counts={designs.motifLayouts}
        label={layoutLabel}
        plainLabel={layoutLabel}
        className="md:col-span-2"
        {...common}
      />
      <Box title="combinaisons" right="modèle × thème × police" className="md:col-span-2">
        <Table
          head={["modèle", "thème", "police", "listes", "récentes"]}
          rows={designs.combos.map((combo) => [
            templateLabel(combo.template),
            { value: <ThemeLabel id={combo.theme} />, num: false },
            { value: fontLabel(combo.font), num: false },
            combo.lists,
            combo.recent,
          ])}
        />
      </Box>
    </Grid>
  );
}
